/**
 * Module Studio — relecture du 18.10 (W2).
 *
 * `ServerUnreachable` : le serveur n'a pas servi `/capabilities` et le profil Vite ne demande pas la
 * démonstration. Le fournisseur de données remplace alors TOUT le contenu par ce panneau : aucune lecture,
 * aucune mutation, un seul geste « Réessayer » (la prochaine lecture réessaie aussi d'elle-même).
 *
 * `DemoProfileBanner` : le profil Vite (`VITE_MODULE_STUDIO_DEMO=true`) a mis tout le Studio en démonstration ;
 * le bandeau le dit, sans bouton (les jeux locaux se réinitialisent par ressource, bandeaux existants).
 */
import { useIntl } from '@/react-intl';
import type { ModuleStudioUnreachableError } from '../data/capabilities';

export type ServerUnreachableProps = {
  error: ModuleStudioUnreachableError;
  onRetry: () => void;
};

export function ServerUnreachable({ error, onRetry }: ServerUnreachableProps) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  return (
    <div
      role="alert"
      data-testid="module-studio-unreachable"
      className="mx-auto my-10 flex max-w-xl flex-col items-center gap-3 rounded-xl border border-portal-danger/40 bg-portal-danger/10 px-6 py-8 text-center"
    >
      <p className="m-0 text-lg font-semibold text-portal-ink">{t('moduleStudio.server.unreachable')}</p>
      <p className="m-0 text-sm text-portal-ink-muted">
        {error.status === null
          ? t('moduleStudio.server.unreachableHint')
          : t('moduleStudio.server.unreachableStatus', { status: error.status })}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="ui-button ui-button-primary mt-2 rounded-md px-4 py-2 text-sm font-medium"
      >
        {t('moduleStudio.server.retry')}
      </button>
    </div>
  );
}

export function DemoProfileBanner() {
  const intl = useIntl();
  return (
    <div
      role="status"
      data-testid="module-studio-demo-profile"
      className="mb-4 rounded-lg border border-info-solid bg-info-subtle px-3 py-2 text-sm text-info-text"
    >
      {intl.formatMessage({ id: 'moduleStudio.demo.profile' })}
    </div>
  );
}

export default ServerUnreachable;
